import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import { api } from '../../lib/api.js'
import { fmtDateTime } from '../../lib/date.js'
import { toast } from '../../components/feedback/Toast.jsx'
import { Linkified } from './ClientCard.jsx'

/**
 * Updates to a client (Charles, 2026-09-26): what a recent session taught
 * us, added under the setup instead of changing it. Each is dated and signed
 * by whoever wrote it, newest first, and the newest is marked Latest — where
 * an update and the setup disagree, the update wins. Updates are never
 * edited; a correction is a new one. The author or an admin can remove one.
 */
export default function ClientUpdates({ client, canEdit, pageKey }) {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const [writing, setWriting] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const notes = client.notes || []
  const user = me.data?.user

  function store(next) {
    qc.setQueryData(pageKey, (d) => (d ? { ...d, client: { ...d.client, notes: next } } : d))
    qc.invalidateQueries({ queryKey: ['clients'] })
  }

  async function save(e) {
    e.preventDefault()
    if (busy || !text.trim()) return
    setBusy(true)
    try {
      const res = await api(`/clients/${client.id}/notes`, { body: { body: text.trim() } })
      store(res.notes)
      setText('')
      setWriting(false)
      toast('Update added')
    } catch (err) {
      toast(err.message || 'Couldn’t add the update', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function remove(n) {
    if (!window.confirm('Remove this update?')) return
    try {
      const res = await api(`/clients/notes/${n.id}`, { method: 'DELETE' })
      store(res.notes)
    } catch (err) {
      toast(err.message || 'Couldn’t remove it', 'error')
    }
  }

  if (notes.length === 0 && !canEdit) return null

  return (
    <section id="client-updates" className="mb-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="section-header">Updates{notes.length > 0 ? ` · ${notes.length}` : ''}</h2>
        {canEdit && !writing && (
          <button onClick={() => setWriting(true)} className="tap flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-link active:bg-grouped">
            <Plus size={14} /> Add update
          </button>
        )}
      </div>

      {writing && (
        <form onSubmit={save} className="card mb-2 p-3">
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="What’s changed or worth knowing from a recent session, e.g. “Now wants 44.1k. Producer is Sam, not Emily.”"
            className="tap w-full rounded-xl border border-hairline bg-grouped px-3 py-2 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-xs text-muted">Signed {user?.name || 'by you'} · today. The setup above stays as it is.</span>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={() => {
                  setWriting(false)
                  setText('')
                }}
                className="tap rounded-lg px-3 py-1.5 text-sm text-muted active:bg-grouped"
              >
                Cancel
              </button>
              <button type="submit" disabled={busy || !text.trim()} className="tap rounded-lg bg-navy px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
                {busy ? 'Saving…' : 'Add'}
              </button>
            </div>
          </div>
        </form>
      )}

      {notes.length > 0 && (
        <div className="card divide-y divide-hairline">
          {notes.map((n, i) => {
            const mine = user && (n.authorId === user.id || user.role === 'admin')
            return (
              <div key={n.id} className="relative px-4 py-3">
                <div className="mb-1 flex items-center gap-2 pr-6 text-xs text-muted">
                  {i === 0 && (
                    <span className="rounded border border-navy px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-navy">Latest</span>
                  )}
                  <span>
                    {fmtDateTime(n.createdAt)} · <span className="font-medium text-ink">{n.author || 'Former account'}</span>
                  </span>
                </div>
                <div className="whitespace-pre-line break-words text-sm text-ink">
                  <Linkified text={n.body} />
                </div>
                {canEdit && mine && (
                  <button
                    onClick={() => remove(n)}
                    aria-label="Remove this update"
                    className="tap absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg text-faint active:bg-grouped"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
      {notes.length === 0 && !writing && (
        <p className="px-1 text-xs text-muted">Learned something in a session that changes the setup? Add it here; it’s dated and signed, and the setup stays as it was.</p>
      )}
    </section>
  )
}
